/**
 * @file        src/pages/hotel/InvoiceDoc.jsx
 * @description Admin-side printable TAX INVOICE for a hotel booking. Rendered
 *              inside the console (no redirect to the customer site) from the admin
 *              API's own booking details. THE SAME DOCUMENT as the guest's copy
 *              (each storefront's src/pages/Invoice): the same per-room charge
 *              table (Hotel / Room / PAX / Nights / Rate / Service Charge / GST /
 *              our fees) with the City / Check-in / Check-out strip, the same
 *              CGST/SGST/IGST heads, the same Gross → Discount → Net summary and
 *              payment breakdown. Mirrors the flight InvoiceDoc's chrome.
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-10-08 Claude — Convenience-fee / Discount lines print the admin-given rule names (fare.convenience_label / fare.discount_label); generic label as fallback.
 *   - 2026-10-06 Claude — Room table: the processing fee is inside Rate and its column is gone; Convenience Fee column always shows. Row values and Gross are unchanged.
 *                          Client decision: the Processing fee (our markup) is not
 *                          a line of its own — it is inside the Base fare, and the
 *                          Convenience fee (shown even at ₹0) takes its place.
 *                          Display only: no total or stored figure changes.
 *   - 2026-09-23 Claude — Three ways this copy disagreed with the guest's copy of
 *                          the SAME invoice, all of them here and none of them in
 *                          the data:
 *                          (a) GST heads. This still picked CGST/SGST vs IGST from
 *                              the HOTEL's state against ours — an accommodation
 *                              place-of-supply rule applied to TripJack's GST on
 *                              its OWN service fee. The guest's copy retired that
 *                              rule on 2026-09-11 and this one did not, so ₹14.40
 *                              printed as IGST here and CGST 7.20 + SGST 7.20
 *                              there. The heads now also carry their rates on the
 *                              same condition (the fee/tax split being present).
 *                          (b) PAN. guestLabel printed `t.pan` unconditionally,
 *                              and the API stamps the booking-level lead PAN onto
 *                              EVERY traveller — so a second guest's name carried
 *                              the first guest's PAN on a tax document. Ported the
 *                              guest copy's guard: a PAN prints only where it is
 *                              demonstrably that guest's own.
 *                          (c) Padded occupancy slots ("Master Guest <surname>",
 *                              written by HotelCheckout so the supplier gets the
 *                              priced headcount) are no longer listed as named
 *                              guests. Same rule now in both storefronts.
 *   - 2026-09-23 Claude — "Back to Bookings" returns to the list this document
 *                          was opened from. This route is shared by the B2C and
 *                          the Corporate screen, so Back always landed on the
 *                          B2C list; lib/docOrigin carries which it was.
 *   - 2026-09-22 Claude — The seller block prints the issuer's GST STATE CODE and
 *     HSN/SAC (Payment Management -> Tax Management). Both were captured, stored and
 *     shown back to the admin but reached NO invoice: the only SAC on any document
 *     was the hardcoded insurance 998559 in the line-item table, so a tenant that
 *     set 998551 never put it in front of a customer. isSet()-guarded like the
 *     GSTIN/PAN/Reg-No rows beside them, so an unconfigured field prints nothing
 *     rather than an empty label.
 *   - 2026-09-10 Claude — The admin copy and the guest's copy of ONE invoice
 *                          disagreed about the booking, so this is now a faithful
 *                          port of the customer document. Four defects went with
 *                          it, all reported off a side-by-side of the two:
 *                          (1) DETAIL — the table billed a booking-level lump on a
 *                          single row against a "Guests" COUNT, where the guest's
 *                          copy itemises ONE ROW PER ROOM naming the actual PAX
 *                          (with PAN), the nights, and the city / check-in /
 *                          check-out strip. Same columns, same per-room split, so
 *                          the two copies now read identically.
 *                          (2) DISCOUNT PRINTED TWICE — it was a table column AND
 *                          a totals line, so a ₹100 discount appeared as ₹200 of
 *                          reduction to anyone reading the document. It is a
 *                          totals line only, as on the guest's copy.
 *                          (3) The trailing "Amount" column is gone. It restated
 *                          the Net, and restated it WRONG: it summed base +
 *                          service + GST + processing + convenience − discount and
 *                          never included `transaction_fee`, which IS inside
 *                          total_paid — so on any booking carrying one, the row's
 *                          own "Amount" contradicted the Net Amount printed
 *                          beneath it.
 *                          (4) GST DETAILS — a Description/Taxable/GST table that
 *                          named no tax head at all. Accommodation GST is CGST +
 *                          SGST when the property is in our registered state and
 *                          IGST when it is not; the place of supply is the
 *                          HOTEL's state (now returned by the details API), so the
 *                          heads are chosen the same way the guest's copy chooses
 *                          them instead of being left unstated.
 *                          Gross, Discount and Net are unchanged in value — Gross
 *                          is now DERIVED from the printed columns (rate +
 *                          supplier fees + our fees) rather than back-solved as
 *                          total + discount, so the line items always add up to
 *                          the stated total.
 *   - 2026-09-10 Claude — Payment Details (wallet / gateway split) now prints,
 *                          via the shared components/DocPaymentSplit. The guest's
 *                          copy has always shown it; the admin copy implied the
 *                          whole amount went through the gateway.
 *   - 2026-09-09 Claude — Top-left mark always renders a LOGO, via the shared
 *                          components/DocBrandMark (storefront logo → tenant
 *                          admin-portal logo → the green BrandWordmark SVG the
 *                          customer site shows). Neither logo column is
 *                          populated on most tenants, so in practice every
 *                          invoice printed the plain-text COMPANY.legal fallback
 *                          instead of a logo. The circular "VERIFIED" stamp
 *                          above the footer is gone — the flight invoice dropped
 *                          it already, and a self-issued verification badge
 *                          asserts nothing on a tax invoice.
 *   - 2026-09-08 Claude — Brand mark added top-left, from the booking's own
 *                          storefront (brands.logo_url) like the e-ticket. The
 *                          invoice printed with no logo at all.
 *   - 2026-09-09 Claude — "Taxes" is now "Service Charge" + "GST", two columns.
 *                          It carried gst_amount, which is the supplier's whole
 *                          "taxes and fees" line — a ₹20-per-room-per-night
 *                          service fee plus 18% GST on that fee — and the GST
 *                          Details table below stated the ROOM BASE as the
 *                          taxable value for it, so a ₹40 fee's ₹7.20 of tax was
 *                          printed as ₹47.20 levied on ₹4,579.74. Same fix, same
 *                          figures, as the customer invoice — the two copies of
 *                          one invoice must not disagree about tax.
 *   - 2026-08-17 Claude — Invoice No. now reads `invoice_number` from the details
 *                          API (the issued series number) instead of deriving one
 *                          from the booking reference. The admin and customer
 *                          copies of the same invoice printed DIFFERENT numbers,
 *                          neither matching the configured series.
 *   - 2026-08-04 Claude — CR D2: accepts an optional `bookingIdOverride` prop
 *                          that short-circuits useParams() — see flight
 *                          TicketDoc.jsx for why (ShareEmailModal off-screen PDF
 *                          capture).
 *   - 2026-07-16 Claude — Initial (admin-native hotel invoice; replaces the deep link).
 *   - 2026-08-10 Claude — Issuer is fully tenant-scoped: the verification stamp
 *                          no longer hardcodes "NEON HOLIDAYS" (it renders
 *                          COMPANY.name). See lib/useCompany.js + migration 051.
 *   - 2026-08-10 Claude — Status gate via docAccess() (an invoice exists only
 *                          for a confirmed or cancelled booking — client-approved
 *                          rule), and the totals block is now arithmetically
 *                          honest: Gross is the pre-discount sum, Discount
 *                          subtracts, Net equals what was actually paid (it used
 *                          to print Gross = Net with a discount "subtracted"
 *                          between them that changed nothing).
 *   - 2026-09-15 Claude — The seller block now prints the issuer's GSTIN, PAN and
 *     registration number, and the Terms list appends whatever the tenant
 *     configured (Payment Management → Tax Management, migration 056). Same gap
 *     as the flight invoice: no supplier GSTIN was printed at all.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../lib/docOrigin';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import { getBookingDetails } from '../../api/hotelBookings';
import { docAccess } from './cells';
import { useCompany } from '../../lib/useCompany';
import { isSet, extraTerms } from '../../lib/company';
import DocBrandMark from '../../components/DocBrandMark';
import DocPaymentSplit from '../../components/DocPaymentSplit';

const n2 = (v) => (Number(v) || 0).toFixed(2);
const rupee = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtDate = (v) => {
  const d = v ? new Date(v) : new Date();
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
// The day-month-year the customer copy prints check-in / check-out in.
const fmtDMY = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const p = (x) => String(x).padStart(2, '0');
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
};

/**
 * Split a booking-level total into `count` equal parts, the LAST part absorbing
 * the rounding remainder so the parts sum to EXACTLY the total. On an invoice the
 * line items must add up to the stated Gross, so a lost paisa is a real defect.
 * Same helper, same behaviour, as the customer copy's.
 */
const splitEven = (total, count) => {
  const c = Math.max(1, count);
  const t = Number(total) || 0;
  const each = r2(t / c);
  const arr = Array.from({ length: c }, () => each);
  arr[c - 1] = r2(t - each * (c - 1));
  return arr;
};

const nameKey = (x) => `${x?.firstName || ''} ${x?.lastName || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * A PADDED OCCUPANCY SLOT is not a named guest.
 *
 * The hotel checkout pads every occupant the customer did not name, so TripJack
 * receives exactly the priced occupancy (HotelCheckout's buildTravellers writes
 * `Guest <lead's surname>`, titled "Master" for a child and "Mr" for an adult).
 * That is a headcount placeholder, not somebody's identity, and a tax invoice may
 * not state it as a named guest beside a real one.
 *
 * The supplier echoes it back with nothing marking it — no flag we invent would
 * survive the round trip — so it is recognised the one way it can be: by the name
 * our own checkout writes. A room's FIRST traveller is its lead and is never
 * treated as padding. Identical rule in both storefronts' Invoice page.
 */
const isPaddedGuest = (t, i) => i > 0 && String(t?.firstName || '').trim().toLowerCase() === 'guest';

/**
 * One guest label, "Mr. Name Surname (PAN: ABCDE1234F)" — but ONLY when that PAN
 * is demonstrably THIS guest's own.
 *
 * The API cannot tell us that directly: the details route returns
 * `pan: t.pan || stored.pan`, so every traveller arrives carrying the
 * BOOKING-LEVEL lead PAN whenever the supplier echoed no per-guest value. This
 * label printed it unconditionally, so a second room booked for somebody else
 * printed the FIRST guest's PAN under their name — one person's tax identity
 * against another person's, on a tax document. The guest's own copy has guarded
 * this since 2026-09-11; this copy never did, which is how the two disagreed.
 *
 * Unlike the guest's copy there is no booking-level `pan` field here, so the
 * lead's PAN is read off the lead traveller — the same value, since that is what
 * the server stamps everywhere.
 */
function makeGuestLabel(lead) {
  const leadPan = String(lead?.pan || '').trim().toUpperCase();
  const leadKey = nameKey(lead);
  return (t) => {
    const nm = `${t.title ? `${t.title}. ` : ''}${t.firstName || ''} ${t.lastName || ''}`.replace(/\s+/g, ' ').trim();
    if (!nm) return '';
    const own = String(t.pan || '').trim().toUpperCase();
    // Either this guest IS the lead (the lead PAN is genuinely theirs), or the
    // value DIFFERS from the lead's, which means a real per-guest PAN was echoed.
    // A co-guest whose PAN cannot be told apart from the lead's prints with NO
    // PAN rather than a wrong one.
    const trusted = own && (own !== leadPan || (leadKey && nameKey(t) === leadKey));
    return trusted ? `${nm} (PAN: ${own})` : nm;
  };
}

export default function InvoiceDoc({ bookingIdOverride } = {}) {
  const { id: routeId } = useParams();
  const id = bookingIdOverride ?? routeId;
  const navigate = useNavigate();
  // Which list to return to — this document route is shared by the B2C and the
  // Corporate screen (lib/docOrigin).
  const { search } = useLocation();
  const COMPANY = useCompany();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['hotel-booking-details', Number(id)],
    queryFn: () => getBookingDetails(id),
  });
  const b = data?.data;

  useEffect(() => { if (b) document.title = `Invoice ${b.booking_reference} · ${COMPANY.name}`; }, [b]);

  if (isLoading) return <div className="panel glass"><div className="empty">Preparing invoice…</div></div>;
  if (isError || !b) return <div className="panel glass"><div className="empty">Could not load this booking.</div></div>;

  // An invoice exists only for a confirmed or cancelled booking (client-approved
  // rule) — gated here too, not just at the buttons, so a pasted URL can't
  // print one for a stay that was never paid for.
  if (!docAccess(b.booking_status).invoice) {
    return (
      <div className="panel glass">
        <div className="empty">
          An invoice is only issued once a booking is confirmed — this booking is “{b.booking_status || 'not confirmed'}”.
          <div style={{ marginTop: 12 }}>
            <button className="btn ghost" onClick={() => navigate(backToList(search, '/hotel/bookings', '/hotel/corporate-bookings'))}>
              <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ref = b.booking_reference || '';
  // The ISSUED number from the configured series (Payment → Invoice Number
  // Series), as stored on the booking's invoice row — the SAME number the
  // customer's copy prints. The derived string is a legacy fallback only, for
  // bookings with no invoice row; it ignored the configured prefix/padding/FY
  // segment and, being ref-derived, could repeat across bookings.
  const invoiceNo = b.invoice_number
    || `HTL-${String(ref).replace(/\D/g, '').slice(-10) || '0'}`;
  const fare = b.fare || {};
  const stay = b.stay || {};

  const hotelName = b.hotel?.name || stay.hotelName || '—';
  const roomName = stay.roomName || b.rooms?.[0]?.name || '—';
  const city = b.hotel?.city || '';
  const nights = stay.nights ?? '—';

  // ---- money, mapped exactly as the guest's copy maps it ----------------------
  //   Rate        = room base fare
  //   Service     = the supplier's fee, and GST = the tax charged ON that fee
  //   Processing /  our own fees, ONE COLUMN EACH, printed only when levied
  //   Convenience /
  //   Transaction
  //   Gross       = Rate + supplier fees + our fees (pre-discount)
  //   Net Amount  = what the guest actually paid (Gross − discount)
  const rate = r2(fare.base_fare);

  // The supplier's "taxes and fees" line, split into the fee and the GST charged
  // on it. The server sends the two apart (utils/hotelServiceFee.js); an older
  // backend sends neither, and then this keeps printing the single combined
  // figure it always printed rather than deriving a second copy of the rule here.
  const hasFeeSplit = fare.service_charge != null && fare.service_charge_gst != null;
  const serviceCharge = hasFeeSplit ? r2(fare.service_charge) : 0;
  const gst = hasFeeSplit ? r2(fare.service_charge_gst) : r2(fare.gst_amount);
  // What the supplier charged in total, however it is being broken out — Gross is
  // built from THIS, so the printed columns add up to the same figure whether or
  // not the split is available.
  const supplierFees = hasFeeSplit ? r2(serviceCharge + gst) : gst;

  const processingFee = r2(fare.processing_fee);
  const convenienceFee = r2(fare.convenience_fee);
  // Inside total_paid, and it used to have no column here at all — which is what
  // made the old trailing "Amount" column disagree with the Net beneath it.
  const transactionFee = r2(fare.transaction_fee);
  const feesTotal = r2(processingFee + convenienceFee + transactionFee);

  // Tax heads mirror the SUPPLIER's own invoice. This tax is TripJack's 18% GST on
  // its own service fee (mft) — NOT accommodation GST — so the intra/inter-state
  // choice is TripJack's, made from its registration against ours, and has nothing
  // to do with where the hotel is. TripJack prints it as CGST + SGST, and the
  // flight invoice has always printed the same fee's GST (MFT) that way, IGST zero.
  //
  // This used to pick the head from the HOTEL's state vs COMPANY.state — an
  // accommodation place-of-supply rule applied to a tax that is not accommodation
  // tax. The guest's copy dropped that rule; this copy did not, so the two copies
  // of ONE invoice disagreed on the same booking: ₹14.40 printed as IGST here and
  // as CGST 7.20 + SGST 7.20 there. Two copies of one invoice disagreeing is the
  // symptom to chase, and the guest's copy was the correct one.
  const cgst = r2(gst / 2);
  const sgst = r2(gst - cgst); // remainder, so cgst + sgst === gst exactly
  const igst = 0;

  // `coupon_discount` from the details API is ALREADY coupon_discount +
  // discount_amount, summed server-side — the whole reduction, not just the
  // coupon. That is what makes Gross − Discount land exactly on total_paid.
  const discount = r2(fare.coupon_discount);
  const gross = r2(rate + supplierFees + feesTotal);
  const net = r2(fare.total_paid);

  const paymentSplit = {
    walletPaid: Number(fare.wallet_paid) || 0,
    gatewayPaid: Number(fare.gateway_paid) || 0,
  };

  // ONE TABLE ROW PER ROOM (the guest's copy prints each booked room on its own
  // line). We store booking-level totals, not per-room prices, so split each money
  // column evenly across the rooms with the remainder on the last row — the rows
  // then sum EXACTLY to Gross.
  const roomList = (b.rooms && b.rooms.length)
    ? b.rooms
    : [{ name: roomName, travellers: b.lead_guest ? [b.lead_guest] : [] }];
  // The booking's lead traveller, for the PAN guard above. `lead_guest` is our own
  // stored copy; the first traveller of the first room is the same person as the
  // supplier echoes them, and carries the stamped PAN when lead_guest has none.
  const guestLabel = makeGuestLabel(
    (b.lead_guest?.pan ? b.lead_guest : null) || roomList[0]?.travellers?.[0] || b.lead_guest || null,
  );
  const roomCount = roomList.length;
  const rateSplit = splitEven(rate, roomCount);
  const svcSplit = splitEven(serviceCharge, roomCount);
  const gstSplit = splitEven(gst, roomCount);
  const procSplit = splitEven(processingFee, roomCount);
  const convSplit = splitEven(convenienceFee, roomCount);
  const txnSplit = splitEven(transactionFee, roomCount);
  const roomRows = roomList.map((rm, i) => ({
    roomType: rm.name || roomName,
    // Padded occupancy slots are dropped — see isPaddedGuest.
    pax: (rm.travellers || []).filter((t, gi) => !isPaddedGuest(t, gi))
      .map(guestLabel).filter(Boolean).join(', ') || '—',
    rate: rateSplit[i] ?? 0,
    serviceCharge: svcSplit[i] ?? 0,
    gst: gstSplit[i] ?? 0,
    processing: procSplit[i] ?? 0,
    convenience: convSplit[i] ?? 0,
    transaction: txnSplit[i] ?? 0,
  }));

  // Rate and GST always print (a 0.00 is a statement; an absent column is not).
  // Each fee column appears only when that fee was actually levied, so an ordinary
  // booking is not padded with three zero columns — and whatever set is shown, the
  // printed columns still add up to Gross exactly, because `gross` is built from
  // these same figures. Neither Discount nor a restated "Amount" is a column: the
  // discount is a totals line (printing it in both places read as double the
  // reduction) and the total is the Net Amount, stated once.
  const columns = [
    { key: 'hotel', label: 'Hotel Name', w: 16, cell: () => hotelName },
    { key: 'roomType', label: 'Room Type', w: 13, cell: (rw) => rw.roomType },
    { key: 'pax', label: 'PAX Name', w: 26, cell: (rw) => rw.pax },
    { key: 'nights', label: 'Nights', w: 6, cell: () => nights },
    // Rate INCLUDES this room's share of our processing fee (no column of its own).
    { key: 'rate', label: 'Rate', w: 12, cell: (rw) => n2((Number(rw.rate) || 0) + (Number(rw.processing) || 0)) },
    // Service Charge sits BEFORE GST, because the GST is charged on it — reading
    // the row left to right should read the way the charge was built up.
    ...(hasFeeSplit ? [{ key: 'serviceCharge', label: 'Service Charge', w: 11, cell: (rw) => n2(rw.serviceCharge) }] : []),
    { key: 'gst', label: 'GST', w: 9, cell: (rw) => n2(rw.gst) },
    // In the place the Processing Fee column used to take — shown even at 0.00.
    { key: 'convenience', label: fare.convenience_label || 'Convenience Fee', w: 11, cell: (rw) => n2(rw.convenience) },
    ...(transactionFee > 0 ? [{ key: 'transaction', label: 'Transaction Fee', w: 11, cell: (rw) => n2(rw.transaction) }] : []),
  ];
  // Normalised to 100% — the column set is variable, so fixed widths that happen
  // to sum to 100 for one particular set do not work.
  const wSum = columns.reduce((s, c) => s + c.w, 0) || 1;
  const colWidths = columns.map((c) => Math.round((c.w / wSum) * 10000) / 100);
  const totalCols = columns.length;

  return (
    <>
      <div className="doc-bar">
        <button className="btn ghost" onClick={() => navigate(backToList(search, '/hotel/bookings', '/hotel/corporate-bookings'))}>
          <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Bookings
        </button>
        <button className="btn primary" onClick={() => window.print()}>
          <Icon name="download" /> Print / Save as PDF
        </button>
      </div>

      <article className="doc-sheet">
        {/* Brand mark top-left, above the centred INVOICE title — the same
            lockup the e-ticket / voucher prints, so every document issued for
            one booking reads as the same family. DocBrandMark owns the
            storefront → tenant → bundled-wordmark fallback chain. */}
        <DocBrandMark brandCode={b.brand} className="doc-brand-logo" />
        <h1 className="doc-title">INVOICE</h1>

        <div className="doc-meta">
          <p>No.: {ref}</p>
          <p>Invoice Date: {fmtDate(b.created_at)}</p>
        </div>
        <p className="doc-invno">Invoice No.: {invoiceNo}</p>

        <hr className="doc-hr" />

        <div className="doc-parties">
          <div>
            <p className="doc-h">From</p>
            <p><b>{COMPANY.legal}</b></p>
            <p><b>Address</b>: {COMPANY.address}</p>
            <p><b>Email</b>: {COMPANY.email}</p>
            <p><b>Phone</b>: {COMPANY.phone}</p>
            {isSet(COMPANY.gstin) ? <p><b>GSTIN</b>: {COMPANY.gstin}</p> : null}
            {isSet(COMPANY.pan) ? <p><b>PAN</b>: {COMPANY.pan}</p> : null}
            {isSet(COMPANY.registrationNo) ? <p><b>Reg. No</b>: {COMPANY.registrationNo}</p> : null}
        {isSet(COMPANY.gstStateCode) ? <p><b>GST State Code</b>: {COMPANY.gstStateCode}</p> : null}
        {isSet(COMPANY.hsn) ? <p><b>HSN/SAC</b>: {COMPANY.hsn}</p> : null}
          </div>
          <div className="doc-to">
            <p className="doc-h">To</p>
            <p><b>Email</b>: {b.contact?.email || b.customer_email || '—'}</p>
            <p><b>Phone</b>: {b.contact?.phone || '—'}</p>
          </div>
        </div>

        <table className="doc-table">
          <colgroup>{colWidths.map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
          <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>
            {roomRows.map((rw, i) => (
              <tr key={i}>
                {columns.map((c) => <td key={c.key}>{c.cell(rw)}</td>)}
              </tr>
            ))}
            {/* City / Check-in / Check-out strip, spanning the row — the stay
                dates were columns of their own here, which is why this table
                could not itemise the rooms the guest's copy itemises. */}
            <tr>
              <td><b>City : {city || '—'}</b></td>
              <td><b>Check In :</b></td>
              <td>{fmtDMY(stay.checkIn)}</td>
              <td><b>Check Out :</b></td>
              <td colSpan={Math.max(1, totalCols - 4)}>{fmtDMY(stay.checkOut)}</td>
            </tr>
          </tbody>
        </table>

        <div className="doc-summary">
          <div>
            <p className="doc-h">GST Details :</p>
            {/* The tax heads — never a bare "GST" with no head named. cgst + sgst
                equals the GST column above exactly.

                The headings carry the statutory rate (CGST 9% + SGST 9% = the 18%
                on the supplier's fee) ONLY when the server sent the fee/tax split:
                without it `gst` is the whole fee-plus-tax lump, which is not 9% of
                anything, and a rate over a figure that is not that rate's tax is a
                rate no authority levies. Same condition, same wording, as the
                guest's copy. */}
            <table className="doc-table doc-gst">
              <thead><tr>
                <th>{`Add CGST${hasFeeSplit ? ' @9%' : ''}`}</th>
                <th>{`Add SGST${hasFeeSplit ? ' @9%' : ''}`}</th>
                <th>{`Add IGST${hasFeeSplit ? ' @18%' : ''}`}</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td>{n2(cgst)}</td>
                  <td>{n2(sgst)}</td>
                  <td>{n2(igst)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Gross → Less: Discount → Net Amount, the same three lines as the
              guest's copy. ONE two-column grid rather than a flex row per line, so
              every amount shares a single right edge and the labels a single left
              edge. Our fees are itemised as their own columns in the table above,
              so they are NOT repeated here — Gross already includes them. */}
          <div className="doc-totals doc-totals-grid">
            <span className="lbl">Gross</span>
            <span className="val">{rupee(gross)}</span>
            {discount > 0 && (
              <>
                <span className="lbl">Less: {fare.discount_label || `Discount${fare.coupon_code ? ` (${fare.coupon_code})` : ''}`}</span>
                <span className="val">- {rupee(discount)}</span>
              </>
            )}
            <span className="lbl sum">Net Amount</span>
            <span className="val sum">{rupee(net)}</span>
          </div>
          {/* How the Net Amount was actually settled — wallet and/or gateway. */}
          <DocPaymentSplit split={paymentSplit} />
        </div>

        <hr className="doc-hr" />

        <div className="doc-terms">
          <p className="doc-h">Terms &amp; Conditions :</p>
          <ul>
            <li>All cases &amp; disputes are subject to {COMPANY.jurisdiction} jurisdiction only.</li>
            <li>Refunds &amp; cancellations are subject to the hotel's policy.</li>
            <li>City tax, resort fee and any additional GST (if applicable) are payable directly to the hotel at check-in/check-out.</li>
            <li>For payments made via a payment gateway (link) or using a credit card, additional gateway charges may apply as per the card / payment mode used.</li>
            {/* Tenant-configured terms (Tax Management), one <li> per line. */}
            {extraTerms(COMPANY.terms).map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>

        <p className="doc-foot">This is a Computer Generated Invoice, No Signature required.</p>
      </article>
    </>
  );
}
